// §virality-P0 — pure builders for the post-generation share-channel deep-links
// and the share copy. Extracted from ShareCard so the viral-loop URL formats are
// unit-pinned and can never silently malform: a broken X-intent / wa.me / UTM URL
// is a DEAD viral channel (the user taps Share, nothing useful opens). Pure +
// exported so the exact formats are tested without rendering the component.

import { t } from './i18n';

/** The proof-led copy that travels with every channel ("…in one sentence…"). */
export function buildShareText(appName: string): string {
  return t('share.text', { name: appName });
}

/** The X (Twitter) / WhatsApp channel deep-link for a given share text + url. */
export function channelShareUrl(channel: 'x' | 'whatsapp', text: string, shareUrl: string): string {
  return channel === 'x'
    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`
    : `https://wa.me/?text=${encodeURIComponent(`${text} ${shareUrl}`)}`;
}

// ─── §S679 — the canonical share identity ───────────────────────────────────

/** The minimal shape canonicalShareUrl needs — a RecentApp or a server app row
 *  both satisfy it structurally. */
export interface ShareIdentity {
  slug?: string | null;
  ownerHandle?: string | null;
  /** The app's preview URL (normally absolute: https://<root>/preview/<appId>/). */
  url: string;
  appId: string;
}

/** Resolve the ORIGIN the share link should live on. Prefer the app's own
 *  absolute preview URL (it carries the real ROOT_DOMAIN the gateway serves),
 *  then the current page origin, then the production brand domain. */
function shareOrigin(appUrl: string): string {
  try {
    const u = new URL(appUrl);
    if (u.protocol === 'http:' || u.protocol === 'https:') return u.origin;
  } catch { /* relative or malformed — fall through */ }
  try {
    if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  } catch { /* SSR / sandboxed */ }
  return 'https://pomela.app';
}

/**
 * The CANONICAL share URL for an app — the identity we want travelling the
 * world: `https://<host>/r/<ownerHandle>/<slug>` when the app has been given
 * a share identity (slug + ownerHandle are minted by POST /v1/apps/:appId/share),
 * else the app's absolute preview URL.
 *
 * NOTE: /r/ links only RESOLVE for public apps (the resolver 404s on private
 * ones — a deliberate deanonymization guard). Callers should pass slug/
 * ownerHandle only when the app is public; for a private app the preview URL
 * is the honest link.
 */
export function canonicalShareUrl(app: ShareIdentity): string {
  const origin = shareOrigin(app.url);
  if (app.slug && app.ownerHandle) {
    return `${origin}/r/${encodeURIComponent(app.ownerHandle)}/${encodeURIComponent(app.slug)}`;
  }
  try {
    return new URL(app.url, origin).toString();
  } catch {
    return app.url;
  }
}
